import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Check, X, QrCode, CreditCard, Copy, Spinner } from "@phosphor-icons/react";
import { SiteTopBar } from "../components/site/SiteTopBar";
import { Footer } from "../components/site/Footer";
import { isLogged, loginMerchant, startSubscription, getPlans } from "../lib/merchantApi";

const idPlaceholder = { cnpj: "00.000.000/0000-00", cpf: "000.000.000-00", email: "seu@email.com" };
const fmt = (v) => `R$ ${Number(v || 0).toFixed(2).replace(".", ",")}`;
const limitLabel = (p) => (p.orders_limit == null ? "Pedidos ilimitados" : `Até ${p.orders_limit} pedidos/mês`);

export default function Planos() {
  const [plans, setPlans] = useState([]);
  const [loadingPlans, setLoadingPlans] = useState(true);
  const [logged, setLogged] = useState(isLogged());
  const [busy, setBusy] = useState("");
  const [pix, setPix] = useState(null);
  const [idType, setIdType] = useState("cnpj");
  const [cred, setCred] = useState({ identifier: "", password: "" });

  useEffect(() => {
    (async () => {
      try {
        const data = await getPlans();
        setPlans(data.plans || []);
      } catch (err) {
        toast.error("Não foi possível carregar os planos");
      } finally {
        setLoadingPlans(false);
      }
    })();
  }, []);

  const doLogin = async (e) => {
    e.preventDefault();
    setBusy("login");
    try {
      await loginMerchant(cred.identifier, cred.password);
      setLogged(true);
      toast.success("Login efetuado!");
    } catch (err) {
      toast.error(err.message || "Falha no login");
    } finally {
      setBusy("");
    }
  };

  const checkout = async (planId, mode) => {
    setBusy(`${planId}-${mode}`);
    try {
      const data = await startSubscription(planId, mode);
      if (mode === "preapproval" && data.checkout_url) {
        window.open(data.checkout_url, "_blank", "noopener");
        toast.success("Abrindo o checkout do Mercado Pago…");
      } else if (mode === "pix" && data.pix) {
        setPix({ ...data.pix, plan: planId });
      } else {
        toast.error("Resposta inesperada do servidor de pagamento");
      }
    } catch (err) {
      toast.error(err.message || "Não foi possível iniciar o pagamento");
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <SiteTopBar />
      <main className="mx-auto max-w-8xl px-5 pb-24 pt-32 md:px-10 md:pt-40">
        <p className="font-mono text-xs font-bold uppercase tracking-[0.2em] text-maraca-amber">Assinatura</p>
        <h1 className="mt-3 font-display text-4xl font-extrabold tracking-tight text-maraca-navy md:text-5xl lg:text-6xl">
          Escolha o plano da sua loja
        </h1>
        <p className="mt-4 max-w-xl text-lg text-maraca-navy/70">
          Ciclo de 30 dias. Pague com Pix na hora ou ative a assinatura recorrente no cartão.
          Os mesmos preços valem para o app e para a web.
        </p>

        {!logged && (
          <form onSubmit={doLogin} className="mt-8 max-w-md rounded-2xl border border-black/10 bg-slate-50 p-6" data-testid="planos-login">
            <h2 className="font-display text-lg font-bold text-maraca-navy">Entre com sua loja</h2>
            <p className="mt-1 text-sm text-maraca-navy/60">Ainda não tem conta? <Link to="/cadastro" className="font-bold text-maraca-teal">Cadastre sua loja</Link>.</p>
            <div className="mt-4 mb-2 flex gap-2">
              {["cnpj", "cpf", "email"].map((t) => (
                <button key={t} type="button" onClick={() => setIdType(t)} className={`rounded-full px-3 py-1 text-xs font-bold uppercase ${idType === t ? "bg-maraca-navy text-white" : "bg-slate-200 text-maraca-navy/70"}`}>{t}</button>
              ))}
            </div>
            <input data-testid="login-identifier" value={cred.identifier} onChange={(e) => setCred((c) => ({ ...c, identifier: e.target.value }))} placeholder={idPlaceholder[idType]} className="mb-3 w-full rounded-xl border border-black/10 px-4 py-3 outline-none focus:border-maraca-teal" />
            <input data-testid="login-password" type="password" value={cred.password} onChange={(e) => setCred((c) => ({ ...c, password: e.target.value }))} placeholder="Senha" className="mb-4 w-full rounded-xl border border-black/10 px-4 py-3 outline-none focus:border-maraca-teal" />
            <button disabled={busy === "login"} data-testid="login-submit" className="w-full rounded-full bg-maraca-navy px-6 py-3 font-display font-bold text-white disabled:opacity-60">
              {busy === "login" ? "Entrando…" : "Entrar"}
            </button>
          </form>
        )}

        {loadingPlans ? (
          <div className="mt-16 flex items-center justify-center gap-3 text-maraca-navy/60">
            <Spinner size={26} className="animate-spin text-maraca-teal" /> Carregando planos…
          </div>
        ) : (
          <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3" data-testid="planos-grid">
            {plans.map((p) => {
              const popular = p.id === "pro";
              return (
                <div key={p.id} data-testid={`plan-${p.id}`} className={`relative flex flex-col rounded-3xl border bg-white p-8 transition-transform duration-300 hover:-translate-y-1 ${popular ? "border-maraca-teal shadow-xl" : "border-black/5"}`}>
                  {popular && (
                    <span className="absolute -top-3 left-8 rounded-full bg-maraca-teal px-3 py-1 text-xs font-bold uppercase tracking-wide text-maraca-navy">Mais popular</span>
                  )}
                  <h3 className="font-display text-2xl font-extrabold text-maraca-navy">{p.name}</h3>
                  <div className="mt-2 flex items-end gap-1">
                    <span data-testid={`plan-price-${p.id}`} className="font-display text-4xl font-extrabold text-maraca-navy">{fmt(p.price)}</span>
                    <span className="mb-1 text-sm text-maraca-navy/50">/mês</span>
                  </div>
                  <p className="mt-1 text-sm font-semibold text-maraca-teal">{limitLabel(p)}</p>
                  <ul className="mt-6 flex-1 space-y-3">
                    {(p.features || []).map((f) => (
                      <li key={f} className="flex items-start gap-2 text-sm text-maraca-navy/75">
                        <Check size={18} weight="bold" className="mt-0.5 shrink-0 text-maraca-teal" /> {f}
                      </li>
                    ))}
                  </ul>
                  <div className="mt-8 space-y-3">
                    <button disabled={!logged || busy.startsWith(p.id)} onClick={() => checkout(p.id, "pix")} data-testid={`pix-${p.id}`}
                      className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-maraca-teal px-5 py-3 font-display font-bold text-maraca-navy transition-transform duration-300 hover:-translate-y-0.5 disabled:opacity-50">
                      <QrCode size={18} weight="fill" /> {busy === `${p.id}-pix` ? "Gerando Pix…" : "Pagar com Pix agora"}
                    </button>
                    <button disabled={!logged || busy.startsWith(p.id)} onClick={() => checkout(p.id, "preapproval")} data-testid={`preapproval-${p.id}`}
                      className="inline-flex w-full items-center justify-center gap-2 rounded-full border-2 border-maraca-navy px-5 py-3 font-display font-bold text-maraca-navy transition-colors hover:bg-maraca-navy hover:text-white disabled:opacity-50">
                      <CreditCard size={18} weight="fill" /> Assinatura recorrente
                    </button>
                  </div>
                  {!logged && <p className="mt-3 text-center text-xs text-maraca-navy/50">Entre para assinar</p>}
                </div>
              );
            })}
          </div>
        )}
      </main>

      {pix && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4" data-testid="pix-modal">
          <div className="w-full max-w-sm rounded-3xl bg-white p-6 text-center">
            <div className="flex items-center justify-between">
              <h3 className="font-display text-xl font-bold text-maraca-navy">Pague com Pix</h3>
              <button onClick={() => setPix(null)} data-testid="pix-close" className="rounded-full p-1 text-maraca-navy/60 hover:bg-slate-100"><X size={22} /></button>
            </div>
            {pix.qr_code_base64 && (
              <img src={`data:image/png;base64,${pix.qr_code_base64}`} alt="QR Code Pix" className="mx-auto mt-4 h-56 w-56" />
            )}
            <p className="mt-3 text-2xl font-extrabold text-maraca-navy">{fmt(pix.amount)}</p>
            <button
              onClick={() => { navigator.clipboard.writeText(pix.qr_code || ""); toast.success("Código Pix copiado!"); }}
              data-testid="pix-copy"
              className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-full bg-maraca-navy px-5 py-3 font-display font-bold text-white">
              <Copy size={18} weight="fill" /> Copiar código Pix
            </button>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}
